/* eslint-disable @next/next/no-img-element */
import { capitalize, startCase } from "lodash";
import { useEffect, useState, useRef } from "react";

import { downloadImage } from "../../services/utils";
import CrossfadeImage from "../CrossfadeImage";
import CreateProgress from "./CreateProgress";
import CreateHistory from "./CreateHistory";
import { ArrowDownTrayIcon } from "@heroicons/react/20/solid";
import { PaintBrushIcon, PrinterIcon } from "@heroicons/react/24/solid";
import CopyLinkButton from "../CopyLinkButton";
import SmallDownloadButton from "../SmallDownloadButton";
import CopySvgButton from "../CopySvgButton";
import Link from "next/link";
import { useReactToPrint } from "react-to-print";


export default function CreateResult(props) {
    const {
        svgUrl,
        isCreating,
        isFallback,
        error,
        status,
        loadingTime,
        identifier,
        onSelectItem,
        showDownloadPng,
        showPrint
    } = props;

    const [selectedItem, setSelectedItem] = useState(null);
    const [hasImage, setHasImage] = useState(false);
    const [imgSrc, setImgSrc] = useState(svgUrl);

    const downloadSVG = () => {
        if (!imgSrc) {
            return;
        }
        downloadImage(imgSrc, "svg");
    };

    const downloadPNG = () => {
        if (!selectedItem && !identifier) {
            return;
        }
        const url = `https://imagedelivery.net/xE-VtsYZUS2Y8MtLMcbXAg/${selectedItem?.identifier || identifier}/orig`;
        console.log("downloadPNG", url);
        downloadImage(url, "png");
    };

    const printImage = () => {
        if (!imgSrc) {
            return;
        }
        reactToPrintFn();
    };

    useEffect(() => {
        let result = null;
        if (svgUrl && !isCreating) {
            result = svgUrl;
        } else if (status === "init") {
            setImgSrc(null);
            return;
        } else if (!svgUrl && identifier) {
            let blurKey = "blurmax";
            if (loadingTime > 40) {
                blurKey = "blur10";
            } else if (loadingTime > 26) {
                blurKey = "blur15";
            } else if (loadingTime > 20) {
                blurKey = "blur20";
            } else if (loadingTime > 16) {
                blurKey = "blur25";
            } else if (loadingTime > 12) {
                blurKey = "blur50";
            } else if (loadingTime > 8) {
                blurKey = "blur75";
            } else if (loadingTime > 4) {
                blurKey = "blur";
            }
            result = `https://imagedelivery.net/xE-VtsYZUS2Y8MtLMcbXAg/${identifier}/${blurKey}`;
        }
        setImgSrc(result);
    }, [isCreating, svgUrl, isFallback, loadingTime, identifier]);

    useEffect(() => {
        if (props.isCreating) {
            setSelectedItem(null);
            setHasImage(false);
        }
    }, [props.isCreating]);


    const contentRef = useRef(null);
    const reactToPrintFn = useReactToPrint({ contentRef });

    return (
        <div className="flex-1 flex items-center justify-center overflow-hidden max-auto mt-8 sm:h-full relative">
            {!isCreating && <div className="hidden sm:block w-48 mr-3"></div>}
            {error && (
                <div className="text-center self-center items-center my-8 text-red-800">
                    {error}
                </div>
            )}
            {!error && imgSrc && props.previewView && (
                <div className="text-center flex justify-center h-full flex-1 sm:flex-none sm:w-[512px] lg:mx-[85.5px]">
                    {isCreating ? null : props.previewView}
                </div>
            )}
            {!error && imgSrc && !props.previewView && (
                <div className="text-center flex justify-center h-full flex-1 sm:flex-none sm:w-[512px] lg:mx-[85.5px]">
                    <div
                        ref={contentRef}
                        className="group relative block w-full max-h-full print-wrapper "
                        onClick={(e) => {
                            if (imgSrc && !props.isCreating) {
                                downloadSVG();
                                e.preventDefault();
                                e.stopPropagation();
                                window.umami?.track(
                                    "Create Result - Download 1",
                                    {}
                                );
                            }
                        }}
                    >
                        {imgSrc?.includes("my.vectorart.ai") ? (
                            <div className="rounded-md overflow-hidden h-full">
                                <img
                                    className="rounded-md height-auto max-h-full w-full"
                                    src={imgSrc}
                                />
                            </div>
                        ) : (
                            <CrossfadeImage
                                containerClass="rounded-md overflow-hidden h-full"
                                imgClass="rounded-md height-auto max-h-full w-full"
                                src={imgSrc}
                                key={identifier}
                                alt={prompt}
                                onLoad={() => setHasImage(true)}
                                duration={
                                    svgUrl || (selectedItem && !isCreating)
                                        ? 300
                                        : 4000
                                }
                            />
                        )}
                        {(svgUrl || selectedItem) && (
                            <div className="absolute top-4 left-4 flex invisible group-hover:visible ">
                                <div className="relative mr-3 cursor-pointer">
                                    <SmallDownloadButton
                                        tooltipPosition={"bottom"}
                                        onClick={(e) => {
                                            if (imgSrc) {
                                                downloadSVG();
                                                e.preventDefault();
                                                e.stopPropagation();
                                                window.umami?.track(
                                                    "Create Result - Download 2",
                                                    {}
                                                );
                                            }
                                        }}
                                    />
                                </div>
                                <div className="relative mr-3 cursor-pointer">
                                    <CopySvgButton
                                        tooltipPosition={"bottom"}
                                        getContent={async () => {
                                            const url = imgSrc || svgUrl;
                                            if (!url) {
                                                return false;
                                            }
                                            const response = await fetch(url);
                                            const text = await response.text();
                                            return text;
                                        }}
                                    />
                                </div>
                                <div className="relative mr-3 cursor-pointer">
                                    <CopyLinkButton
                                        tooltipPosition={"bottom"}
                                        link={
                                            selectedItem
                                                ? `https://vectorart.ai/${selectedItem?.svg}`
                                                : `https://vectorart.ai/${
                                                      svgUrl.includes(
                                                          "my.vectorart.ai"
                                                      )
                                                          ? svgUrl
                                                                .split("/")
                                                                .slice(-1)[0]
                                                          : svgUrl
                                                                .split("/")
                                                                .slice(-2)[0]
                                                  }`
                                        }
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {isCreating && (
                <CreateProgress status={status} hasImage={hasImage} />
            )}
            {!isCreating &&
                !svgUrl &&
                !error &&
                !selectedItem &&
                (props.initialComponent ? (
                    props.initialComponent
                ) : (
                    <div className="flex sm:mx-32 flex-col items-center justify-end sm:h-full xl:w-[427px]">
                        <div className="mb-8">
                            <img
                                alt="Enter your prompt"
                                src={`/sparkle.svg`}
                                className="w-52 opacity-10 dark:opacity-40"
                            />
                        </div>
                        <div className="flex flex-col items-center justify-center">
                            <h1 className="text-4xl font-bold text-gray-700 dark:text-gray-100 mb-2 mt-8">
                                Start Creating
                            </h1>
                            <h3 className="text-gray-500 dark:text-gray-400 mb-8">
                                Enter your prompt to get started
                            </h3>
                        </div>
                    </div>
                ))}
            {!isCreating && (
                <div className="hidden sm:flex flex-col w-48 self-start ml-3 h-full">
                    {props.dropzone && imgSrc ? (
                        <div className="flex-1">{props.dropzone}</div>
                    ) : null}
                    <CreateHistory
                        isMinimal={props.isMinimal}
                        latest={isCreating ? null : svgUrl}
                        onSelect={(item) => {
                            const svgUrl = item.fallback
                                ? `https://my.vectorart.ai/storage/v1/object/public/vectorart-upload/${item.svg}`
                                : `https://imagedelivery.net/xE-VtsYZUS2Y8MtLMcbXAg/${item.svg}/sm`;
                            setImgSrc(svgUrl);
                            setSelectedItem(item);
                            onSelectItem?.(item);
                        }}
                    />
                    {!props.isMinimal && (
                        <div className="mt-3 ml-1 flex-1 ">
                            <p className="pb-6 sm:pt-0 sm:pb-2 text-gray-700 font-light text-sm hidden lg:block dark:text-gray-400">
                                {capitalize(
                                    selectedItem?.prompt?.slice(0, 110)
                                ) +
                                    (selectedItem?.prompt?.length > 110
                                        ? "..."
                                        : "")}
                            </p>
                            {selectedItem?.mode && (
                                <div className="inline-block px-2 py-0.5 text-gray-700 text-xs rounded-lg bg-gray-300 dark:bg-gray-700 dark:text-gray-300">
                                    {startCase(selectedItem.mode)}
                                </div>
                            )}
                        </div>
                    )}
                    {imgSrc && (
                        <div className="align-bottom mt-3">
                            <div className="focus:outline-none">
                                <a
                                    href={`/edit/${
                                        selectedItem?.svg ||
                                        svgUrl?.split("/").slice(-2)[0]
                                    }`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="focus:outline-none"
                                >
                                    <button
                                        type="button"
                                        className="flx items-center text-left border border-gray-300 dark:border-gray-700  bg-white disabled:bg-gray-500 inline-flex w-full justify-center rounded-md  px-4 py-2 font-medium text-black  focus:outline-none sm:col-start-2 text-xs md:text-sm  hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                                    >
                                        <PaintBrushIcon className="w-3 h-3 mr-4" />
                                        <div className="flex-1">Edit image</div>
                                    </button>
                                </a>
                            </div>
                            <div>
                                <button
                                    onClick={(e) => {
                                        if (imgSrc) {
                                            downloadSVG();
                                            e.preventDefault();
                                            window.umami?.track(
                                                "Create Result - Download 3",
                                                {}
                                            );
                                        }
                                    }}
                                    type="button"
                                    className="mt-3 text-left items-center bg-white disabled:bg-gray-500 inline-flex w-full shadow-sm justify-center rounded-md border border-gray-300 dark:border-gray-700  px-4 py-2 font-medium text-black  focus:outline-none sm:col-start-2 text-xs md:text-sm dark:bg-gray-800 dark:text-gray-100   hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                    <ArrowDownTrayIcon className="h-4 w-4 mr-3" />
                                    <div className="flex-1">Download SVG</div>
                                </button>
                            </div>
                            {showDownloadPng && (
                                <div>
                                    <button
                                        onClick={(e) => {
                                            if (imgSrc) {
                                                downloadPNG();
                                                e.preventDefault();
                                                window.umami?.track(
                                                    "Create Result - Download PNG",
                                                    {}
                                                );
                                            }
                                        }}
                                        type="button"
                                        className="mt-3 text-left items-center bg-white disabled:bg-gray-500 inline-flex w-full shadow-sm justify-center rounded-md border border-gray-300 dark:border-gray-700  px-4 py-2 font-medium text-black  focus:outline-none sm:col-start-2 text-xs md:text-sm dark:bg-gray-800 dark:text-gray-100   hover:bg-gray-100 dark:hover:bg-gray-700"
                                    >
                                        <ArrowDownTrayIcon className="h-4 w-4 mr-3" />
                                        <div className="flex-1">
                                            Download PNG
                                        </div>
                                    </button>
                                </div>
                            )}
                            {showPrint && (
                                <div>
                                    <button
                                        onClick={(e) => {
                                            if (imgSrc) {
                                                printImage();
                                                e.preventDefault();
                                                window.umami?.track(
                                                    "Create Result - Print Image",
                                                    {}
                                                );
                                            }
                                        }}
                                        type="button"
                                        className="mt-3 text-left items-center bg-white disabled:bg-gray-500 inline-flex w-full shadow-sm justify-center rounded-md border border-gray-300 dark:border-gray-700  px-4 py-2 font-medium text-black  focus:outline-none sm:col-start-2 text-xs md:text-sm dark:bg-gray-800 dark:text-gray-100   hover:bg-gray-100 dark:hover:bg-gray-700"
                                    >
                                        <PrinterIcon className="h-4 w-4 mr-3" />
                                        <div className="flex-1">
                                            Print Image
                                        </div>
                                    </button>
                                </div>
                            )}
                            <div className="mt-6 text-xs mb-1 text-gray-500 hidden lg:block">
                                <b>VectorArt.ai License</b>
                                <br />
                                Commercial use permitted with a{" "}
                                <Link href="/pricing">
                                    <span className="cursor-pointer">
                                        paid subscription
                                    </span>
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
